import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Loader2, AlertTriangle } from 'lucide-react';
import api from '../api/client';
import { toast } from '../hooks/use-toast';

/**
 * Dialog to issue a credit note (avoir) against a validated invoice.
 * - Default amount = remaining credit (invoice amount - already credited)
 * - User can specify a partial amount and a reason
 * - Backend generates A-YYYY-NNNN sequentially
 */
export default function CreditNoteDialog({ open, onOpenChange, invoice, onIssued }) {
  const srcAmount = Number(invoice?.amount || 0);
  const alreadyCredited = Number(invoice?.credited_amount || 0);
  const remaining = Math.max(0, srcAmount - alreadyCredited);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    if (open) { setAmount(remaining.toFixed(2)); setReason(''); }
  }, [open, remaining]);

  const parsedAmount = Math.max(0, Math.min(remaining, Number(amount) || 0));
  const isPartial = parsedAmount + 0.009 < remaining;

  const submit = async (e) => {
    e.preventDefault();
    if (parsedAmount <= 0) { toast({ title: 'Montant invalide', description: 'Saisissez un montant > 0.' }); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/invoices/${invoice.id}/credit-note`, {
        amount: parsedAmount,
        reason: reason.trim(),
      });
      toast({
        title: 'Avoir émis',
        description: `${data.id} · ${Number(data.amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € ${isPartial ? 'partiel' : 'total'}`,
      });
      onIssued?.(data);
      onOpenChange(false);
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Émission impossible' });
    }
    setSubmitting(false);
  };

  if (!invoice) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="credit-note-dialog">
        <DialogHeader>
          <DialogTitle>Émettre un avoir</DialogTitle>
          <DialogDescription>
            Sur la facture <strong>{invoice.id}</strong> — {invoice.client}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-3 gap-2 text-[11.5px]">
            <div className="p-2 rounded-lg bg-[#F6F7FB]">
              <div className="text-[10px] uppercase tracking-wide text-[#8E8FA5] font-semibold">Facture</div>
              <div className="font-bold text-[#1A1B2E]">{srcAmount.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</div>
            </div>
            <div className="p-2 rounded-lg bg-[#FEF2F2]">
              <div className="text-[10px] uppercase tracking-wide text-[#B91C1C] font-semibold">Déjà crédité</div>
              <div className="font-bold text-[#B91C1C]">{alreadyCredited.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</div>
            </div>
            <div className="p-2 rounded-lg bg-[#EEF2FF]">
              <div className="text-[10px] uppercase tracking-wide text-[#4F46E5] font-semibold">Restant</div>
              <div className="font-bold text-[#4F46E5]">{remaining.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</div>
            </div>
          </div>

          <div>
            <label className="block text-[11.5px] font-semibold text-[#6B6C85] uppercase tracking-wide mb-1.5">
              Montant de l'avoir (€ TTC)
            </label>
            <input
              type="number" step="0.01" min="0.01" max={remaining}
              value={amount} onChange={(e) => setAmount(e.target.value)}
              data-testid="credit-note-amount"
              className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] text-right focus:bg-white focus:border-[#B91C1C] outline-none"
              required
            />
            <div className="mt-1 flex items-center justify-between text-[11px]">
              <span className={isPartial ? 'text-[#B45309]' : 'text-[#0F766E]'}>
                {isPartial ? 'Avoir partiel' : 'Avoir total — la facture sera marquée Annulée'}
              </span>
              <button type="button" onClick={() => setAmount(remaining.toFixed(2))} className="text-[#4F46E5] font-semibold hover:underline">
                Total ({remaining.toFixed(2)} €)
              </button>
            </div>
          </div>

          <div>
            <label className="block text-[11.5px] font-semibold text-[#6B6C85] uppercase tracking-wide mb-1.5">
              Motif (visible sur l'avoir)
            </label>
            <textarea
              rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Erreur de facturation, remise commerciale, etc."
              data-testid="credit-note-reason"
              className="w-full px-3 py-2 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[12.5px] resize-none focus:bg-white focus:border-[#B91C1C] outline-none"
            />
          </div>

          <div className="p-2.5 rounded-lg bg-[#FEF3E7] border border-[#FED7AA] flex items-start gap-2 text-[11.5px] text-[#7C2D12]">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              Un avoir est un document comptable définitif (numéro <strong>A-YYYY-NNNN</strong>).
              Il ne pourra pas être supprimé une fois émis.
            </div>
          </div>

          <DialogFooter>
            <button type="button" onClick={() => onOpenChange(false)} className="h-10 px-4 rounded-xl bg-[#F6F7FB] text-[#4A4B63] text-[13px] font-semibold">Annuler</button>
            <button type="submit" disabled={submitting} data-testid="credit-note-submit" className="h-10 px-5 rounded-xl bg-[#B91C1C] hover:bg-[#991B1B] text-white text-[13px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-60">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Émettre l'avoir
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
